// /app/frontend/src/components/IdentityGateModal.jsx
/**
 * Identity Verification Gate Modal
 * 
 * Shows when user attempts to purchase Elite/Elite+ without identity verification.
 * Explains why verification is required and provides CTA to start verification.
 */
import React, { useState, useContext } from 'react';
import { X, Shield, CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { AuthContext } from '../App';
import { startIdentityVerification, refreshIdentityStatus, getIdentityStatusString } from '../api/identity';

export default function IdentityGateModal({ isOpen, onClose, onVerified }) {
  const { token, user, setUser } = useContext(AuthContext);
  const [isStarting, setIsStarting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');
  
  const identityStatus = getIdentityStatusString(user);
  const isPending = identityStatus === 'pending';
  const isFailed = identityStatus === 'failed';
  
  if (!isOpen) return null;

  const handleStartVerification = async () => {
    setError('');
    setIsStarting(true);
    
    try {
      const result = await startIdentityVerification(token);
      if (result.url) {
        // Redirect to Stripe Identity verification
        window.location.href = result.url;
      }
    } catch (e) {
      console.error('Identity verification error:', e);
      setError(e.message || 'Failed to start verification. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  const handleRefreshStatus = async () => {
    setError('');
    setIsRefreshing(true);
    
    try {
      const result = await refreshIdentityStatus(token);
      // Update user context with new identity status
      if (result?.identity && setUser) {
        setUser(prevUser => ({
          ...prevUser,
          identity: result.identity
        }));
      }
      
      // If now verified, close modal and proceed
      if (result.identity?.status === 'verified') {
        onVerified?.();
        onClose();
      }
    } catch (e) {
      console.error('Refresh status error:', e);
      setError('Failed to check status. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-slate-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-full bg-amber-500/20">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">
              Identity Verification Required
            </h2>
          </div>

          {/* Status-specific content */}
          {isPending ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-200 font-medium">Verification in progress</p>
                  <p className="text-xs text-blue-300/70 mt-1">
                    Your identity verification is being processed. This usually takes a few minutes.
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-slate-300">
                If you&apos;ve already completed verification, click below to check your status.
              </p>
              
              <p className="text-xs text-slate-400 italic">
                💡 If verification looks stuck, tap &quot;Start over with new verification&quot; below.
              </p>
            </div>
          ) : isFailed ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-200 font-medium">Verification unsuccessful</p>
                  <p className="text-xs text-red-300/70 mt-1">
                    Your previous verification attempt was not successful. You can try again.
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-slate-300">
                Please ensure you have a valid government-issued ID ready and good lighting.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-300">
                To keep Unity Love safe and real, we require identity verification before purchasing Elite memberships.
              </p>
              
              <div className="space-y-2">
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Why we verify:</p>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>Prevents fake accounts and catfishing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>Protects our community from bad actors</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>Creates a more intentional dating experience</span>
                  </li>
                </ul>
              </div>
              
              <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                <p className="text-xs text-slate-400">
                  <strong className="text-slate-300">What you&apos;ll need:</strong> A valid government-issued ID (passport, driver&apos;s license, or national ID card) and a device with a camera.
                </p>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-6 flex flex-col gap-3">
            {isPending ? (
              <>
                <button
                  onClick={handleRefreshStatus}
                  disabled={isRefreshing}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-blue-500 hover:bg-blue-400 disabled:bg-blue-500/50 text-white font-semibold transition-colors"
                >
                  {isRefreshing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    'Check Verification Status'
                  )}
                </button>
                <button
                  onClick={handleStartVerification}
                  disabled={isStarting}
                  className="w-full px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Start over with new verification
                </button>
              </>
            ) : (
              <button
                onClick={handleStartVerification}
                disabled={isStarting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 text-white font-semibold transition-colors"
              >
                {isStarting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    Verify My Identity
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
            
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
